'use client';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { listProductsWithOptions } from '@/app/apis';
import { PRODUCTS_LIST_DEFAULT_DATA, SITE, TEAM_PRODUCTS_LIST_DEFAULT_DATA } from '@/app/constants';
import { useStore } from '@/app/providers/store';
import type { SubscriptionInfo } from '@/app/stores/slices/user-info';
import type {
  ClawPassProduct,
  ClawPassType,
  CreditV1PeriodProduct,
  CreditV1TeamBundleProduct,
  NopeakPackProduct,
  PaidTrialInfo,
  PeriodType,
  ProductList,
  ProductPayStatusType,
  ProductPlan,
  SiteType,
  SubscriptionProductType,
  SubsPlanType,
  TabItemType,
  TeamPeriodType,
  TeamProductList,
  TeamTabItemType,
  TeamTier,
  UserInfo,
} from '@/app/types';
import {
  buildMonthlyOriginalByVariant,
  getClawPassProductStatus,
  getMaxSidebarDiscount,
  getMaxYearlyProductDiscount,
  getPaidTrialInfo,
  getPriceDiscount,
  getProductStatus,
  getProductVariantKey,
  getStrikethroughPrice,
} from '@/app/utils';
import { useBonusCode } from './use-bonus-code';
import { useFetch } from './use-fetch';
import { useMembershipRevision } from './use-membership-revision';

// 个人版 ProductList 接收的合法 plan_type（不含 team_v2：企业版按 tier 独立建模，见 buildTeamProductMap）
const VALID_PLAN_TYPES = new Set(['free', 'standard_v2', 'premium_v2', 'ultimate_v2']);
const VALID_BILLING_PERIODS = new Set(['monthly', 'quarterly', 'yearly']);
const VALID_PRODUCT_TYPES = new Set(['pass', 'subscription']);
/** Synthetic PeriodType value used only on the client for the paid-trial tab. */
export const PAID_TRIAL_PERIOD = 'paid_trial' as PeriodType;

// 企业版按 tier 分档（与个人版按 plan_type 分档对应），billing_period 只支持季 / 年
const VALID_TEAM_TIERS = new Set<TeamTier>(['basic', 'plus', 'pro']);
const VALID_TEAM_BILLING_PERIODS = new Set(['quarterly', 'yearly']);

interface BuildProductMapResult {
  products: ProductList;
  /** 每个 period 下的最大优惠百分比，如 30 表示最多优惠 30%（即 7 折） */
  maxPercentOffByPeriod: Record<PeriodType, number>;
  initialPeriod: PeriodType | null;
  periodKeys: PeriodType[];
}

/** 按 period 分组商品，保留旗舰版档位，并收集最大优惠百分比和当前订阅对应的 initialPeriod。 */
function buildProductMap(
  productList: CreditV1PeriodProduct[],
  subscription: SubscriptionInfo | null,
  userInfo: UserInfo | null,
  clawPass: ClawPassType | null,
  isNewMembership = false,
): BuildProductMapResult {
  let initialPeriod: PeriodType | null = null;
  const periodKeys: PeriodType[] = [];
  const maxPercentOffByPeriod: Record<PeriodType, number> = {
    subscription_monthly: 0,
    subscription_quarterly: 0,
    subscription_yearly: 0,
    pass_monthly: 0,
    pass_quarterly: 0,
    pass_yearly: 0,
  };
  const products = { ...PRODUCTS_LIST_DEFAULT_DATA };
  // 划线价的基准索引：国内按「按月购买」原价，海外连续包年叠加时按「连续包月」原价。
  const monthlyOriginalByVariant = {
    subscription: buildMonthlyOriginalByVariant(productList, 'subscription'),
    pass: buildMonthlyOriginalByVariant(productList, 'pass'),
  };

  for (const product of productList) {
    const { product_type, billing_period, plan_type, id } = product;

    if (
      !VALID_PRODUCT_TYPES.has(product_type) ||
      !VALID_BILLING_PERIODS.has(billing_period) ||
      !VALID_PLAN_TYPES.has(plan_type)
    ) {
      continue;
    }

    const key = `${product_type}_${billing_period}` as PeriodType;
    // 每次响应独立构建，避免默认模板中残留上一轮的商品档位。
    if (!periodKeys.includes(key)) products[key] = { ...products[key] };
    const previousUltimate = products[key].ultimate_v2;
    if (subscription?.product_id === id) {
      initialPeriod = key;
    }

    // 国内连续包月与按月购买共用同一原价基准；海外仍使用连续包月自身 original_price。
    const originalPrice =
      SITE === 'china' && product_type === 'subscription' && billing_period === 'monthly'
        ? (monthlyOriginalByVariant.pass.get(getProductVariantKey(product)) ?? 0)
        : (product.original_price ?? 0);
    // percentOff: 30 = 优惠 30%（中文 7 折），赋值给产品的 discount 字段（类型约束来自 SubscriptionProductType）
    const percentOff = getPriceDiscount(product.price ?? 0, originalPrice);
    const mappedProduct: SubscriptionProductType = {
      ...product,
      discount: percentOff,
      product_pay_status: getProductStatus(product, subscription, clawPass, isNewMembership),
      plan_type: plan_type as SubsPlanType,
      // 划线价只服务卡面对照展示，与 original_price 解耦，避免影响「下个月续费金额」。
      strikethrough_price: getStrikethroughPrice(product, monthlyOriginalByVariant, SITE),
    };
    products[key][plan_type as keyof ProductPlan] = mappedProduct;
    if (plan_type === 'ultimate_v2' && previousUltimate) {
      const variants = previousUltimate.variants ?? [previousUltimate];
      products[key].ultimate_v2 = {
        ...mappedProduct,
        variants: [...variants.filter((item) => item.id !== id), mappedProduct].sort(
          (a, b) => a.credits_monthly_gift - b.credits_monthly_gift,
        ),
      };
    }

    if (billing_period !== 'yearly' && percentOff > maxPercentOffByPeriod[key]) {
      maxPercentOffByPeriod[key] = percentOff;
    }

    if (!periodKeys.includes(key)) {
      periodKeys.push(key);
      fillPlaceholderPlans(products, key, product, subscription, userInfo);
    }
  }

  // 年付折扣与订阅页包年标签共用同一套「年付价 vs 月付原价×12」口径。
  maxPercentOffByPeriod.subscription_yearly = getMaxYearlyProductDiscount(productList, 'subscription');
  maxPercentOffByPeriod.pass_yearly = getMaxYearlyProductDiscount(productList, 'pass');

  return { products, maxPercentOffByPeriod, initialPeriod, periodKeys };
}

/**
 * 为指定 period 补充 free 占位卡片。
 *
 * 注：team_v2 占位卡片已下线，企业版套餐改由后端 team_products 字段单独返回，
 * 由调用方根据 edition 切换渲染。
 */
function fillPlaceholderPlans(
  products: ProductList,
  key: PeriodType,
  templateProduct: CreditV1PeriodProduct,
  subscription: SubscriptionInfo | null,
  userInfo: UserInfo | null,
) {
  products[key].free = {
    ...templateProduct,
    sub_change_id: '0',
    price: 0,
    original_price: 0,
    available: false,
    plan_type: 'free',
    product_pay_status: !subscription?.product_id && userInfo?.subs_plan !== 'team_v2' ? 'current' : 'unavailable',
    discount: 0,
  };
}

interface BuildTeamProductMapResult {
  products: TeamProductList;
  /** 每个 team period 下的最大优惠百分比，如 36 表示最多 36% off */
  maxPercentOffByPeriod: Record<TeamPeriodType, number>;
  initialPeriod: TeamPeriodType | null;
  periodKeys: TeamPeriodType[];
}

/**
 * 与 buildProductMap 结构对齐的企业版分组器：
 * - 按 `team_${billing_period}` 拆 period（命名空间用 team_ 前缀，避免与个人版 PeriodType 冲突）；
 * - 第二层用 `tier`（basic/plus/pro）作为索引，因为企业版所有 SKU 的 plan_type 都是 team_v2；
 * - product_pay_status 由本函数统一派生，UI 只看该字段，无需再读 store 里的 activeBundle：
 *   - `current`：tier + billing_period 与 activeBundle 完全匹配；
 *   - `unavailable`：已持有任一团队 bundle（其他档位/周期不可重复购买）或后端显式 available=false；
 *   - `available`：可购买。
 * - 命中 current 的 period 会作为 initialPeriod，让默认 tab 落到当前持有的周期。
 */
function buildTeamProductMap(
  productList: CreditV1PeriodProduct[],
  activeBundle: CreditV1TeamBundleProduct | null,
): BuildTeamProductMapResult {
  const periodKeys: TeamPeriodType[] = [];
  const maxPercentOffByPeriod: Record<TeamPeriodType, number> = {
    team_yearly: 0,
    team_quarterly: 0,
  };
  const products: TeamProductList = {
    team_yearly: { ...TEAM_PRODUCTS_LIST_DEFAULT_DATA.team_yearly },
    team_quarterly: { ...TEAM_PRODUCTS_LIST_DEFAULT_DATA.team_quarterly },
  };

  const activeTier = activeBundle?.tier;
  const activeBillingPeriod = activeBundle?.billing_period;
  const hasActiveBundle = Boolean(activeTier && activeTier !== 'unspecified');
  let initialPeriod: TeamPeriodType | null = null;

  for (const product of productList) {
    const { billing_period } = product;
    const tier = product.tier as TeamTier | undefined;

    if (!tier || !VALID_TEAM_TIERS.has(tier) || !VALID_TEAM_BILLING_PERIODS.has(billing_period)) {
      continue;
    }

    const key = `team_${billing_period}` as TeamPeriodType;
    const isCurrent = hasActiveBundle && activeTier === tier && activeBillingPeriod === billing_period;
    const status: ProductPayStatusType = isCurrent
      ? 'current'
      : hasActiveBundle || product.available === false
        ? 'unavailable'
        : 'available';

    if (isCurrent) initialPeriod = key;

    const percentOff = getPriceDiscount(product.price ?? 0, product.original_price ?? 0);
    products[key][tier] = {
      ...product,
      discount: percentOff,
      product_pay_status: status,
      plan_type: product.plan_type as SubsPlanType,
    };

    if (percentOff > maxPercentOffByPeriod[key]) {
      maxPercentOffByPeriod[key] = percentOff;
    }

    if (!periodKeys.includes(key)) {
      periodKeys.push(key);
    }
  }

  return { products, maxPercentOffByPeriod, initialPeriod, periodKeys };
}

interface SubscriptionData {
  products: ProductList | null;
  tabList: TabItemType[];
  initialPeriod: PeriodType | null;
  /** Present when any subscription_products entry has paid_trial. */
  paidTrialInfo: PaidTrialInfo | null;
}

const INITIAL_SUBSCRIPTION_DATA: SubscriptionData = {
  products: null,
  tabList: [],
  initialPeriod: null,
  paidTrialInfo: null,
};

interface TeamSubscriptionData {
  products: TeamProductList | null;
  tabList: TeamTabItemType[];
  initialPeriod: TeamPeriodType | null;
}

const INITIAL_TEAM_SUBSCRIPTION_DATA: TeamSubscriptionData = {
  products: null,
  tabList: [],
  initialPeriod: null,
};

export const useFetchProductList = () => {
  const t = useTranslations();
  const isNewMembership = useMembershipRevision();
  const previousRevision = useRef<boolean | undefined>(undefined);

  const {
    bonusCode,
    subscription,
    activeTeamBundleProduct,
    refreshGetProducts,
    updateRefreshGetProducts,
    updateMaxProductDiscount,
    userInfo,
    clawPass,
  } = useStore((state) => ({
    bonusCode: state.bonusCode,
    subscription: state.subscription,
    activeTeamBundleProduct: state.activeTeamBundleProduct,
    refreshGetProducts: state.refreshGetProducts,
    updateRefreshGetProducts: state.updateRefreshGetProducts,
    updateMaxProductDiscount: state.updateMaxProductDiscount,
    userInfo: state.userInfo,
    clawPass: state.clawPass,
  }));
  const previousUserId = useRef(userInfo?.id);

  const { handleBonusCode } = useBonusCode();

  const [clawPassProducts, setClawPassProducts] = useState<ClawPassProduct[]>([]);
  const [nopeakPackProducts, setNopeakPackProducts] = useState<NopeakPackProduct[]>([]);
  const [subscriptionData, setSubscriptionData] = useState<SubscriptionData>(INITIAL_SUBSCRIPTION_DATA);
  const [teamSubscriptionData, setTeamSubscriptionData] =
    useState<TeamSubscriptionData>(INITIAL_TEAM_SUBSCRIPTION_DATA);

  /**
   * 根据接口返回的优惠数据生成 tab 列表。
   * percentOff: 30 = 优惠 30%（中文显示"最低7折"）。
   */
  const buildTabList = (periodKeys: PeriodType[], maxPercentOffByPeriod: Record<PeriodType, number>): TabItemType[] => {
    const tabTemplates: Record<SiteType, TabItemType[]> = {
      china: [
        { value: 'pass_yearly', label: '按年购买' },
        { value: 'subscription_monthly', label: '连续包月' },
        { value: 'pass_monthly', label: '按月购买' },
      ],
      global: [
        { value: 'subscription_yearly', label: t('payments.plans.subscriptionYear') },
        { value: 'subscription_monthly', label: t('payments.plans.subscriptionMonth') },
      ],
    };

    return tabTemplates[SITE]
      .filter((tab) => periodKeys.includes(tab.value))
      .map((tab) => {
        const percentOff = maxPercentOffByPeriod[tab.value];
        if (percentOff > 0) {
          return {
            ...tab,
            // percentOff=30 → (100-30)/10=7 → "最低7折"
            tooltip:
              SITE === 'china' ? `最低${(100 - percentOff) / 10}折` : t('payments.plans.off', { num: percentOff }),
            isDiscount: true,
            discount: percentOff,
          };
        }
        return { ...tab };
      });
  };

  /**
   * 企业版 tab list 构建：
   * 与个人版 buildTabList 同形——按可用 period 过滤模板、按 maxPercentOff 注入 tooltip。
   * 视觉文案统一走 i18n（billingYearly / billingQuarterly），不区分 site。
   */
  const buildTeamTabList = (
    periodKeys: TeamPeriodType[],
    maxPercentOffByPeriod: Record<TeamPeriodType, number>,
  ): TeamTabItemType[] => {
    const tabTemplates: TeamTabItemType[] = [
      { value: 'team_yearly', label: t('payments.plans.billingYearly') },
      { value: 'team_quarterly', label: t('payments.plans.billingQuarterly') },
    ];

    return tabTemplates
      .filter((tab) => periodKeys.includes(tab.value))
      .map((tab) => {
        const percentOff = maxPercentOffByPeriod[tab.value];
        if (percentOff > 0) {
          return {
            ...tab,
            tooltip:
              SITE === 'china' ? `最低${(100 - percentOff) / 10}折` : t('payments.plans.off', { num: percentOff }),
            isDiscount: true,
            discount: percentOff,
          };
        }
        return { ...tab };
      });
  };

  const {
    loading: loadingProducts,
    error,
    run: getProductsList,
  } = useFetch(
    () =>
      listProductsWithOptions({
        query: {
          channels: SITE === 'global' ? ['stripe'] : ['alipay'],
          bonus_code: bonusCode || undefined,
        },
      }),
    {
      manual: true,
      onBefore: () => setNopeakPackProducts([]),
      onSuccess: ({ data }) => {
        if (!data) return;

        setNopeakPackProducts(
          (data.nopeak_pack_products ?? []).map((product) => ({
            ...product,
            product_pay_status: product.available === true ? 'available' : 'unavailable',
          })),
        );

        setClawPassProducts(
          (data.claw_pass_products ?? []).map((v) => ({
            ...v,
            product_pay_status: getClawPassProductStatus(v),
            plan_type: v.plan_type as SubsPlanType,
          })),
        );

        const rawProducts = [...(data.subscription_products ?? []), ...(data.pass_products ?? [])];
        // 兑换/切换券后重新拉列表时，同步刷新营销文案的「最高X折」
        updateMaxProductDiscount(getMaxSidebarDiscount(rawProducts, SITE));
        const { products, maxPercentOffByPeriod, initialPeriod, periodKeys } = buildProductMap(
          rawProducts,
          subscription,
          userInfo,
          clawPass,
          isNewMembership,
        );
        const tabList = buildTabList(periodKeys, maxPercentOffByPeriod);

        // 试用档判定与开屏弹窗共用同一函数，避免两处口径漂移
        const paidTrialInfo = getPaidTrialInfo(data.subscription_products ?? []);

        // Inject the paid-trial tab when eligible
        const finalTabList: TabItemType[] = paidTrialInfo
          ? [
              ...tabList,
              {
                value: PAID_TRIAL_PERIOD,
                label: t('payments.plans.newUserTrial'),
                paidTrial: paidTrialInfo,
              },
            ]
          : tabList;

        setSubscriptionData({
          products,
          tabList: finalTabList,
          // 有试用时优先默认选中试用 tab，否则按订阅状态或第一个 tab
          initialPeriod: (paidTrialInfo ? PAID_TRIAL_PERIOD : null) ?? initialPeriod ?? finalTabList[0]?.value ?? null,
          paidTrialInfo,
        });

        // 企业版按"次包/新购"语义：不参与升降级，已订阅同 SKU 即 current、其余统一 unavailable / available。
        // pay() 据此走 createNewOrder，跳过 ChangeContent 升降级变更弹窗。
        const {
          products: teamProducts,
          maxPercentOffByPeriod: teamMaxPercentOffByPeriod,
          initialPeriod: teamInitialPeriod,
          periodKeys: teamPeriodKeys,
        } = buildTeamProductMap(data.team_products ?? [], activeTeamBundleProduct);
        const teamTabList = buildTeamTabList(teamPeriodKeys, teamMaxPercentOffByPeriod);
        setTeamSubscriptionData({
          products: teamProducts,
          tabList: teamTabList,
          initialPeriod: teamInitialPeriod ?? teamTabList[0]?.value ?? null,
        });

        updateRefreshGetProducts(false);
        if (data.bonus_code && SITE === 'china') handleBonusCode(data.bonus_code);
      },
    },
  );

  useEffect(() => {
    const catalogChanged =
      previousUserId.current !== userInfo?.id ||
      (previousRevision.current !== undefined &&
        isNewMembership !== undefined &&
        previousRevision.current !== isNewMembership);
    previousUserId.current = userInfo?.id;
    previousRevision.current = isNewMembership;
    if (!refreshGetProducts && !catalogChanged) return;
    if (refreshGetProducts) updateRefreshGetProducts(false);
    getProductsList();
  }, [userInfo?.id, isNewMembership, refreshGetProducts, updateRefreshGetProducts, getProductsList]);

  return {
    getProductsList,
    loading: loadingProducts,
    error,
    clawPassProducts,
    nopeakPackProducts,
    subscriptionProducts: subscriptionData.products,
    subscriptionTabList: subscriptionData.tabList,
    subscriptionInitialPeriod: subscriptionData.initialPeriod,
    subscriptionPaidTrialInfo: subscriptionData.paidTrialInfo,
    teamSubscriptionProducts: teamSubscriptionData.products,
    teamSubscriptionTabList: teamSubscriptionData.tabList,
    teamSubscriptionInitialPeriod: teamSubscriptionData.initialPeriod,
  };
};
